import React from "react";
import { useFormContext } from "react-hook-form";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import { TransferDirectionEnum } from "../../enums/TransferDirectionEnum";
import { TransportEnum, DepartureTransportSelect } from "../../enums/TransferTransportEnum";
import DepartureLocationFrom from "../locationComponents/DepartureLocationFrom";
import DepartureLocationTo from "../locationComponents/DepartureLocationTo";
import VehiclePricesSelector from "../vehiclePricesComponents/VehiclePricesSelector";
import DepartureTransportCompanyAndNumber from "../referenceComponents/DepartureTransportCompanyAndNumber";
import NumberOfPassengersField from "../passengerComponents/NumberOfPassengersField";
import TransportDate from "../dateAndTimeComponents/TransportDate";
import TransportTime from "../dateAndTimeComponents/TransportTime";
import PickUpTime from "../dateAndTimeComponents/PickUpTime";
import Department from "../../types/Department";
import EventCity from "../../../eventCities/types/EventCity";

type Props = {
    departureTransport: number;
    departureRoute: any;
    departureRouteLoading?: boolean;
    errors: any;
    control: any;
    department?: Department;
    eventCity?: EventCity;
};

const DepartureTransferSection: React.FC<Props> = ({ 
    departureTransport, 
    departureRoute,
    departureRouteLoading = false,
    errors, 
    control,
    department,
    eventCity,
}) => {
    const t = useTranslate();
    const { watch } = useFormContext();
    const showPickUpTime = department?.name?.toLowerCase() !== "package";
    
    // Watch location values
    const departureLocationFrom = watch("departureLocationFrom");
    const departureLocationTo = watch("departureLocationTo");
    
    // Only show alert if it's an "individuals" department
    const isIndividuals = department?.name?.toLowerCase() === "individuals"
        || department?.name?.toLowerCase() === "individual";
    
    // Show alert only if: (is OTHER or no routes) AND both locations are selected AND is individuals AND NOT loading
    const showCustomRouteAlert = !departureRouteLoading && isIndividuals && (Number(departureTransport) === TransportEnum.OTHER || !departureRoute?.supplierRouteVehiclePrices?.length) 
        && departureLocationFrom && departureLocationTo;

    return (
        <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="pb-4">
                <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {t("transfers.fields.returnTransfer")}
                </h4>
            </div>

            {/* Transport Type */}
            <DepartureTransportSelect control={control} errors={errors} t={t} />

            {/* Transport Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TransportDate name="departureTransportDate" transportFieldName="departureTransport" direction="departure" eventCity={eventCity} />
                <TransportTime name="departureTransportTime" transportFieldName="departureTransport" direction="departure" dateFieldName="departureTransportDate"/>
                <NumberOfPassengersField name="departureNumberOfPassengers" direction="departure" />
            </div>

            {/* Transport Reference Fields */}
            {Number(departureTransport) !== TransportEnum.OTHER && (
                <DepartureTransportCompanyAndNumber />
            )}

            {/* Location Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DepartureLocationFrom />
                <DepartureLocationTo />
            </div>

            {/* Vehicle Selection */}
            {departureRoute?.supplierRouteVehiclePrices?.length > 0 && departureTransport !== TransportEnum.OTHER && (
                <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <VehiclePricesSelector
                        vehicles={departureRoute.supplierRouteVehiclePrices || []}
                        fieldName="departureVehicle"
                        label={t("transfers.message.selectVehicleDeparture")}
                        direction={TransferDirectionEnum.DEPARTURE}
                    />
                </div>
            )}

            {/* Alert for no routes or OTHER transport */}
            {showCustomRouteAlert && (
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <div className="flex gap-3">
                        <svg className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-medium text-orange-800 dark:text-orange-100">
                                {t("transfers.message.customRoute") || "We will look into the cost for this personalised route for you and send you the price by email within 24 hrs"}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Pick Up Time */}
            {showPickUpTime && (
                <PickUpTime />
            )}
        </div>
    );
};

export default DepartureTransferSection;

